import { of, isNonEmpty, headNonEmpty, tailNonEmpty, prepend } from '../../Chunk/dist/effect-Chunk.esm.js';
import { right, left } from '../../Either/dist/effect-Either.esm.js';
import { equals } from '../../Equal/dist/effect-Equal.esm.js';
import { dual } from '../../Function/dist/effect-Function.esm.js';
import { Structural } from '../Data.esm.js';

/** @internal */
const OrPatchTypeId = /*#__PURE__*/Symbol.for("effect/DifferOrPatch");
function variance(a) {
  return a;
}

/** @internal */
const PatchProto = {
  ...Structural.prototype,
  [OrPatchTypeId]: {
    _Value: variance,
    _Key: variance,
    _Patch: variance
  }
};

/** @internal */

const EmptyProto = /*#__PURE__*/Object.assign( /*#__PURE__*/Object.create(PatchProto), {
  _tag: "Empty"
});
const _empty = /*#__PURE__*/Object.create(EmptyProto);

/** @internal */
const empty = () => _empty;

/** @internal */

const AndThenProto = /*#__PURE__*/Object.assign( /*#__PURE__*/Object.create(PatchProto), {
  _tag: "AndThen"
});

/** @internal */
const makeAndThen = (first, second) => {
  const o = Object.create(AndThenProto);
  o.first = first;
  o.second = second;
  return o;
};

/** @internal */

const SetLeftProto = /*#__PURE__*/Object.assign( /*#__PURE__*/Object.create(PatchProto), {
  _tag: "SetLeft"
});

/** @internal */
const makeSetLeft = value => {
  const o = Object.create(SetLeftProto);
  o.value = value;
  return o;
};

/** @internal */

const SetRightProto = /*#__PURE__*/Object.assign( /*#__PURE__*/Object.create(PatchProto), {
  _tag: "SetRight"
});

/** @internal */
const makeSetRight = value => {
  const o = Object.create(SetRightProto);
  o.value = value;
  return o;
};

/** @internal */

const UpdateLeftProto = /*#__PURE__*/Object.assign( /*#__PURE__*/Object.create(PatchProto), {
  _tag: "UpdateLeft"
});

/** @internal */
const makeUpdateLeft = patch => {
  const o = Object.create(UpdateLeftProto);
  o.patch = patch;
  return o;
};

/** @internal */

const UpdateRightProto = /*#__PURE__*/Object.assign( /*#__PURE__*/Object.create(PatchProto), {
  _tag: "UpdateRight"
});

/** @internal */
const makeUpdateRight = patch => {
  const o = Object.create(UpdateRightProto);
  o.patch = patch;
  return o;
};
/** @internal */
const diff = options => {
  switch (options.oldValue._tag) {
    case "Left":
      {
        switch (options.newValue._tag) {
          case "Left":
            {
              const valuePatch = options.left.diff(options.oldValue.left, options.newValue.left);
              if (equals(valuePatch, options.left.empty)) {
                return empty();
              }
              return makeUpdateLeft(valuePatch);
            }
          case "Right":
            {
              return makeSetRight(options.newValue.right);
            }
        }
      }
    case "Right":
      {
        switch (options.newValue._tag) {
          case "Left":
            {
              return makeSetLeft(options.newValue.left);
            }
          case "Right":
            {
              const valuePatch = options.right.diff(options.oldValue.right, options.newValue.right);
              if (equals(valuePatch, options.right.empty)) {
                return empty();
              }
              return makeUpdateRight(valuePatch);
            }
        }
      }
  }
};

/** @internal */
const combine = /*#__PURE__*/dual(2, (self, that) => makeAndThen(self, that));

/** @internal */
const patch = /*#__PURE__*/dual(2, (self, {
  left: left$1,
  oldValue,
  right: right$1
}) => {
  let patches = of(self);
  let result = oldValue;
  while (isNonEmpty(patches)) {
    const head = headNonEmpty(patches);
    const tail = tailNonEmpty(patches);
    switch (head._tag) {
      case "Empty":
        {
          patches = tail;
          break;
        }
      case "AndThen":
        {
          patches = prepend(head.first)(prepend(head.second)(tail));
          break;
        }
      case "UpdateLeft":
        {
          if (result._tag === "Left") {
            result = left(left$1.patch(head.patch, result.left));
          }
          patches = tail;
          break;
        }
      case "UpdateRight":
        {
          if (result._tag === "Right") {
            result = right(right$1.patch(head.patch, result.right));
          }
          patches = tail;
          break;
        }
      case "SetLeft":
        {
          result = left(head.value);
          patches = tail;
          break;
        }
      case "SetRight":
        {
          result = right(head.value);
          patches = tail;
          break;
        }
    }
  }
  return result;
});

export { OrPatchTypeId, combine, diff, empty, makeAndThen, makeSetLeft, makeSetRight, makeUpdateLeft, makeUpdateRight, patch };
